import { getOrCreateMainLogger } from "../logger/main";
import { downloadFile as doDownload } from "./download";
import { appDataReleasesPath } from "./appDataFolder";
import { StandardResponse } from "../types/types";
import { deferred } from "../util";
import fs from "fs";
import path from "path";
import { AutoUpdater, autoUpdater, Event, FeedURLOptions } from "electron";
const logger = getOrCreateMainLogger();

let createHash: Function;
try {
	createHash = require("crypto").createHash;
} catch (err) {
	logger.error("crypto support is disabled!");
}

/**
 * Strips the byte order mark from a string
 *
 * The Unicode Standard permits the BOM in UTF-8, but does not require or recommend its use. Byte order has no meaning
 * in UTF-8, so its only use in UTF-8 is to signal at the start that the text stream is encoded in UTF-8.
 *
 * @param utf8String
 * @returns {string}
 */
const stripBom = (utf8String: string) => {
	if (utf8String.charCodeAt(0) === 0xfeff) {
		return utf8String.slice(1);
	}

	return utf8String;
};

type Listener = (event: Event, releaseNotes: string, releaseName: string, releaseDate: Date, updateURL: string) => void;

type EventName =
	| "update-not-available"
	| "checking-for-update"
	| "error"
	| "before-quit-for-update"
	| "update-available"
	| "update-downloaded";

/**
 * Gets the SHA1 checksum of the file passed in
 * @param fileName
 * @returns {Promise<string>}
 */
const getFileChecksum = (fileName: any) =>
	new Promise<string>((resolve) => {
		let checksum = createHash("sha1");

		// Updating checksum with file content
		let stream = fs.createReadStream(fileName);
		stream.on("data", (data) => {
			checksum.update(data);
		});

		// making digest
		stream.on("end", () => {
			resolve(checksum.digest("hex"));
		});
	});

/**
 * Extend the existing autoUpdater, keeping the same interface as the original AutoUpdater.
 * Modify it's behaviour by downloading the files locally before and using the local directory instead of the updateUrl
 * Downloads the update files locally using the same proxies that the rest of the application and chrome uses
 * 1. Download the RELEASES file from the updateUrl
 * 2. Parse the RELEASES and get all the entries
 * 3. Download the entries and check the checksum on the local files.
 * 4. Perform update as normal using the local directory instead of the updateUrl
 */
class AutoUpdaterWrapper {
	autoUpdater: AutoUpdater;

	useProxy: boolean = false;

	feedURL?: FeedURLOptions;

	localCache: FeedURLOptions = { url: "" };

	releaseFileChecksum?: string;

	latestReleaseFileName?: string;

	updateAvailabilityPromise: Promise<boolean>;

	constructor() {
		this.autoUpdater = autoUpdater;
		this.bindCorrectContext();

		const { promise, resolve } = deferred<boolean>();
		this.updateAvailabilityPromise = promise;
		this.autoUpdater.on("update-available", () => {
			logger.debug("Resolving update available to true");
			resolve(true);
		});
		this.autoUpdater.on("update-not-available", () => {
			logger.debug("Resolving update available to false");
			resolve(false);
		});
		this.autoUpdater.on("error", (error: Error) => {
			logger.error(`There was an error checking for updates: ${error.message}`);
			logger.debug("Resolving update available to false");
			resolve(false);
		});
	}

	bindCorrectContext() {
		this.onReleaseFileDownloaded = this.onReleaseFileDownloaded.bind(this);
		this.validateLocalFiles = this.validateLocalFiles.bind(this);
	}

	on(event: EventName, listener: Listener): AutoUpdaterWrapper {
		autoUpdater.on(event as any, listener);
		return this;
	}

	quitAndInstall() {
		this.autoUpdater.quitAndInstall();
	}

	setFeedURL(options: FeedURLOptions): void {
		this.feedURL = options;
		this._setFeedUrl();
	}

	getUpdateAvailabilityPromise(): Promise<boolean> {
		return this.updateAvailabilityPromise;
	}

	_setFeedUrl() {
		if (this.feedURL) {
			if (this.useProxy) {
				if (!this.localCache.url) {
					this.localCache = { ...this.feedURL };
					this.localCache.url = appDataReleasesPath();
				}
				this.autoUpdater.setFeedURL(this.localCache);
			} else {
				this.autoUpdater.setFeedURL(this.feedURL);
			}
		}
	}

	downloadViaElectron(downloadViaElectron: boolean): void {
		if (downloadViaElectron && !createHash) {
			logger.error(
				"node crypto support is disabled! Unable to download via electron and validate files. Will attempt normal upgrade process"
			);
			downloadViaElectron = false;
		}
		this.useProxy = downloadViaElectron;
		this._setFeedUrl();
	}

	async downloadFile(params: any) {
		const filePath = path.join(params.toPath, params.fileName);
		if (fs.existsSync(filePath)) {
			logger.info(`Update file exists "${filePath}", deleting`);
			fs.unlinkSync(filePath);
		}

		return await doDownload(params);
	}

	checkForUpdates(): void {
		if (this.useProxy) {
			this._checkForUpdates();
		} else {
			this.autoUpdater.checkForUpdates();
		}
	}

	/**
	 * Prepares a local directory by downloading the RELEASES file and all of the files referenced in there. Then passes
	 * control onto the normal squirrel/electron upgrade process and uses the local directory instead of the remote
	 * updateUrl.
	 */
	_checkForUpdates() {
		// Extracting this path out of the function cause it out to use the default Electron path

		let fromPath = "";
		try {
			const url = new URL(this.feedURL?.url ?? "");
			url.pathname = path.join(url.pathname, "RELEASES");
			fromPath = url.href;
		} catch (e) {
			logger.error("Feed URL is invalid", this.feedURL?.url, e);
			return;
		}

		logger.debug("Downloading RELEASES file from:", fromPath);
		this.autoUpdater.emit("checking-for-update");

		// Download RELEASES file
		const params = {
			fileName: "RELEASES",
			fromPath,
			toPath: this.localCache.url,
		};

		this.downloadFile(params)
			.then(this.onReleaseFileDownloaded)
			.then(() => this.removeOlderVersions())
			.catch((error: any) => {
				logger.error(error);
				this.autoUpdater.emit("update-not-available");
			});
	}

	removeOlderVersions() {
		const releasesDir = this.localCache.url;
		fs.readdirSync(releasesDir).forEach((file) => {
			if (file !== "RELEASES" && file !== this.latestReleaseFileName) {
				const filePathToDelete = path.join(releasesDir, file);
				logger.info(`Deleting old release file: ${filePathToDelete}`);
				fs.unlinkSync(filePathToDelete);
			}
		});
	}

	async onReleaseFileDownloaded(releasesObj: StandardResponse) {
		const { err: releaseError, data: releasesFile } = releasesObj;
		if (releaseError) {
			throw releaseError;
		} else if (!releasesFile) {
			throw `Failed to download Releases file`;
		}
		// Release file (re)downloaded, Check if it's the same as the previous time we downloaded
		const currentChecksum = await getFileChecksum(releasesFile);
		const instance = this;

		if (currentChecksum !== this.releaseFileChecksum) {
			const fileData = fs.readFileSync(releasesFile, { encoding: "utf-8", flag: "r" });
			const entries = await this.getReleaseEntriesToDownload(fileData);

			await Promise.all(
				entries.map(async (entry) => {
					const fileParams = {
						fileName: entry.fileName,
						fromPath: `${instance.feedURL?.url}/${entry.fileName}`,
						toPath: instance.localCache.url,
					};
					const { err, data: downloadedFileName } = await this.downloadFile(fileParams);

					if (err) {
						throw err;
					} else if (!downloadedFileName) {
						throw `Failed to download ${entry.fileName}`;
					}

					try {
						const hashIsValid = await instance.hashIsValid(downloadedFileName, entry.sha1);
						if (hashIsValid) return true;
					} catch (e) {
						logger.error(e);
						throw new Error(`${downloadedFileName} is not valid`);
					}

					throw new Error(`${downloadedFileName} hash is not valid`);
				})
			)
				.then(() => {
					instance.releaseFileChecksum = currentChecksum;
					logger.log("All downloads complete");
					instance.autoUpdater.checkForUpdates();
				})
				.catch((error) => {
					/**
					 * A cases exists here where there could have been a error downloading one of the files. Either because
					 * HTTP errors or because they don't exist.
					 *
					 * Treat this as a successful it's possible that this is the current state of the remote server.
					 * Log the error and let electron/autoUpdater take over and continue the flow
					 */
					logger.error(`Failed to download one or some of the package files. \n${error}`);
					instance.autoUpdater.emit("update-not-available");
				});
			return;
		}

		instance.autoUpdater.emit("update-not-available");
	}

	/**
	 * Gets the release entries in the RELEASES file give it's contents
	 * @param fileContents
	 */
	async getReleaseEntriesToDownload(fileContents: any) {
		let releases: any[] = [];
		// Strip the byte order mark as is done in the electron auto updater.
		fileContents = stripBom(fileContents);

		if (!fileContents) {
			return releases;
		}

		// Split by newline
		const entries = fileContents.split("\n");

		// parse release entries and filter out null values
		releases = entries.map(this.parseReleaseEntry).filter((entry: any) => entry !== null);

		// get the newest available release
		if (releases.length > 1) {
			logger.info(`Multiple release entries found. Getting the latest one`);
		}
		// ASSUMPTION: We are assuming that the last entry in the RELEASES file is the latest one
		const latestRelease = releases[releases.length - 1];
		logger.info(`Latest release found :: ${latestRelease.fileName}`);
		this.latestReleaseFileName = latestRelease.fileName;
		return await this.validateLocalFiles([latestRelease]);
	}

	/**
	 * Checks to see if previously downloaded entries match the hash in the release file.
	 *
	 * @param entries
	 */
	async validateLocalFiles(entries: any) {
		return (
			await Promise.all(
				entries.map(async (entry: any) => {
					if (!(await this.hashIsValid(path.join(this.localCache.url, entry.fileName), entry.sha1))) {
						return entry;
					}
				})
			)
		).filter((x) => !!x);
	}

	/**
	 * Checks the file passed in matches the sha1 checksum passed in
	 *
	 * @param fileName
	 * @param sha1
	 */
	async hashIsValid(fileName: string, sha1: string): Promise<boolean> {
		logger.info(`validating ${fileName}`);
		if (!fs.existsSync(fileName)) {
			return false;
		} else {
			return sha1.toLowerCase() === (await getFileChecksum(fileName)).toLowerCase();
		}
	}

	/**
	 * Takes a line from the RELEASES file and parses it into an object.
	 *
	 * Taken from from here: https://github.com/Squirrel/Squirrel.Windows/blob/develop/src/Squirrel/ReleaseEntry.cs#L95-L96
	 *
	 * Gerard P: According to the above, there exists a case where the fileName can be a URL. I've not seen this in the
	 * wild and under what conditions the full url would be written to the file, but if this does occur, it would mean
	 * we would have to download the file from that url and create and write a new RELEASES file so that it points to
	 * the correct local path.
	 */
	parseReleaseEntry(entry: any) {
		const entryRegex = /^([0-9a-fA-F]{40})\s+(\S+)\s+(\d+)[\r]*$/;
		const commentRegex = /\s*#.*$/;
		if (!entry) {
			return null;
		}

		entry = entry.replace(commentRegex, "");
		if (!entry) {
			return null;
		}

		const matchObject = entry.match(entryRegex);

		if (!matchObject || (matchObject && matchObject.length !== 4)) {
			logger.error(`Invalid entry in RELEASES file: ${entry}`);
			return null;
		} else {
			// We do not support delta upgrades, therefore we ignore it from the RELEASES entries
			if ((matchObject[2] as string).endsWith("-delta.nupkg")) {
				return null;
			}

			return {
				sha1: matchObject[1],
				fileName: matchObject[2],
				filesize: Number.parseInt(matchObject[3], 10),
			};
		}
	}
}

export const autoUpdaterWrapper = new AutoUpdaterWrapper();
